import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { MessageOutlined, EllipsisOutlined } from '@ant-design/icons';
import { Dropdown, Menu, Button, Spin, Empty } from 'antd';
import { createDifyChatAPI, DifyDataTransformer } from '../utils/difyChat';
import DifyAppService from '../utils/difyAppService';
import { getUserId } from '../utils/storage';
import './chat-workspace.css';

/** 平台 Agent 使用数字 id，走后端代理避免 CORS */
const isPlatformAgentId = (id) => id != null && /^\d+$/.test(String(id));

function groupConversationsByTime(conversations) {
  const now = Date.now();
  const ONE_DAY = 24 * 60 * 60 * 1000;
  const groups = {
    今日: [],
    '近7日': [],
    '近30天': [],
    '近一年': [],
    其他: [],
  };
  conversations.forEach((conv) => {
    const t = conv.updatedAt || 0;
    if (now - t < ONE_DAY) {
      groups['今日'].push(conv);
    } else if (now - t < 7 * ONE_DAY) {
      groups['近7日'].push(conv);
    } else if (now - t < 30 * ONE_DAY) {
      groups['近30天'].push(conv);
    } else if (now - t < 365 * ONE_DAY) {
      groups['近一年'].push(conv);
    } else {
      groups['其他'].push(conv);
    }
  });
  return groups;
}

const ChatHistoryList = forwardRef(({
  conversations: externalConversations = [],
  currentId,
  onSelect,
  onDelete,
  groupFn = groupConversationsByTime,
  renderGroupTitle,
  renderItem,
  sidebarCollapsed = false,
  useDifyHistory = false,
  agentId,
  difyBaseURL,
  difyApiKey,
  userInfo,
  onConversationsLoaded,
  onConversationDeleted,
}, ref) => {
  const [difyConversations, setDifyConversations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [hoveredId, setHoveredId] = useState(null);
  const onConversationsLoadedRef = useRef(onConversationsLoaded);

  useEffect(() => {
    onConversationsLoadedRef.current = onConversationsLoaded;
  }, [onConversationsLoaded]);

  const conversations = useDifyHistory ? difyConversations : externalConversations;
  const useBackendProxy = useDifyHistory && isPlatformAgentId(agentId);

  const fetchDifyConversations = useCallback(async () => {
    if (!useDifyHistory || !userInfo) {
      setDifyConversations([]);
      return;
    }

    if (!useBackendProxy && !difyApiKey) {
      setDifyConversations([]);
      return;
    }

    setLoading(true);
    setLoadError(null);
    try {
      const userId = getUserId(userInfo);
      const allConversations = [];
      let lastId = '';
      let hasMore = true;
      const pageLimit = 50;

      while (hasMore) {
        let response;
        if (useBackendProxy) {
          response = await DifyAppService.listConversations(agentId, {
            user_id: userId,
            last_id: lastId,
            limit: pageLimit,
          });
        } else {
          const difyAPI = createDifyChatAPI(
            difyBaseURL || process.env.REACT_APP_DIFY_WEB_BASE_URL || 'https://kb.blooart.cn',
            difyApiKey,
          );
          response = await difyAPI.getConversations(userId, lastId, pageLimit);
        }

        const items = response?.data || [];
        const transformed = items.map(DifyDataTransformer.transformConversation);
        allConversations.push(...transformed);

        hasMore = response?.has_more === true && items.length > 0;
        if (hasMore) {
          lastId = items[items.length - 1].id;
        }
      }

      setDifyConversations(allConversations);
      onConversationsLoadedRef.current?.(allConversations);
    } catch (error) {
      console.error('加载 Dify 对话记录失败:', error);
      setLoadError(error?.message || '加载失败');
      setDifyConversations([]);
    } finally {
      setLoading(false);
    }
  }, [useDifyHistory, useBackendProxy, agentId, difyApiKey, difyBaseURL, userInfo]);

  useEffect(() => {
    fetchDifyConversations();
  }, [fetchDifyConversations]);

  const addNewConversation = useCallback((conversation) => {
    if (!conversation?.id) return;
    setDifyConversations((prev) => {
      const exists = prev.some((c) => c.id === conversation.id);
      if (exists) {
        return prev.map((c) => (c.id === conversation.id ? { ...c, ...conversation } : c));
      }
      return [conversation, ...prev];
    });
  }, []);

  const handleDelete = useCallback(async (conversationId) => {
    if (!conversationId) return;

    if (useDifyHistory && userInfo && (useBackendProxy || difyApiKey)) {
      try {
        const userId = getUserId(userInfo);
        if (useBackendProxy) {
          await DifyAppService.deleteConversation(agentId, {
            conversation_id: conversationId,
            user_id: userId,
          });
        } else {
          const difyAPI = createDifyChatAPI(
            difyBaseURL || process.env.REACT_APP_DIFY_WEB_BASE_URL || 'https://kb.blooart.cn',
            difyApiKey,
          );
          await difyAPI.deleteConversation(conversationId, userId);
        }

        setDifyConversations((prev) => {
          const remaining = prev.filter((c) => c.id !== conversationId);
          onConversationDeleted?.(conversationId, remaining);
          return remaining;
        });
      } catch (error) {
        console.error('删除 Dify 对话失败:', error);
      }
    } else {
      onDelete?.(conversationId);
    }
  }, [
    useDifyHistory,
    useBackendProxy,
    agentId,
    difyApiKey,
    difyBaseURL,
    userInfo,
    onDelete,
    onConversationDeleted,
  ]);

  useImperativeHandle(ref, () => ({
    addNewConversation,
    refreshConversations: fetchDifyConversations,
  }), [addNewConversation, fetchDifyConversations]);

  const groups = groupFn(conversations);

  if (loading) {
    return (
      <div className="chat-conv-sidebar__loading">
        <Spin size="small" />
      </div>
    );
  }

  if (sidebarCollapsed) {
    return (
      <div style={{ flex: 1, overflowY: 'auto', padding: '8px 0' }}>
        {conversations.map((conv) => (
          <div
            key={conv.id}
            style={{
              padding: '8px',
              margin: '4px',
              cursor: 'pointer',
              textAlign: 'center',
              borderRadius: 8,
              background: currentId === conv.id ? '#f5f3ff' : 'transparent',
            }}
            onClick={() => onSelect?.(conv.id)}
          >
            <MessageOutlined style={{ fontSize: 16, color: currentId === conv.id ? '#6366f1' : '#94a3b8' }} />
          </div>
        ))}
      </div>
    );
  }

  if (conversations.length === 0) {
    return (
      <div className="chat-conv-sidebar__empty">
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={loadError ? '对话记录加载失败' : '暂无对话记录'}
        />
      </div>
    );
  }

  return (
    <div>
      {Object.entries(groups).map(([group, items]) =>
        items.length > 0 ? (
          <div key={group} style={{ marginBottom: 4 }}>
            <div className="chat-conv-group__title">
              <span className="chat-conv-group__dot" />
              {renderGroupTitle ? renderGroupTitle(group) : group}
            </div>
            {items.map((conv, idx) =>
              renderItem ? (
                renderItem(conv, idx, group)
              ) : (
                <div
                  key={conv.id}
                  className={`chat-conv-item${currentId === conv.id ? ' chat-conv-item--selected' : ''}`}
                  onClick={() => onSelect?.(conv.id)}
                  onMouseEnter={() => setHoveredId(conv.id)}
                  onMouseLeave={() => setHoveredId(null)}
                >
                  <span className="chat-conv-item__label">
                    <MessageOutlined className="chat-conv-item__icon" />
                    {conv.title}
                  </span>
                  <div
                    className="chat-conv-item__menu"
                    style={{
                      opacity: hoveredId === conv.id ? 1 : 0,
                      visibility: hoveredId === conv.id ? 'visible' : 'hidden',
                    }}
                  >
                    <Dropdown
                      overlay={(
                        <Menu>
                          <Menu.Item
                            key="delete"
                            danger
                            onClick={(e) => {
                              e.domEvent.stopPropagation();
                              handleDelete(conv.id);
                            }}
                          >
                            删除
                          </Menu.Item>
                        </Menu>
                      )}
                      trigger={['click']}
                      placement="bottomRight"
                    >
                      <Button
                        type="text"
                        size="small"
                        icon={<EllipsisOutlined />}
                        onClick={(e) => e.stopPropagation()}
                      />
                    </Dropdown>
                  </div>
                </div>
              ),
            )}
          </div>
        ) : null,
      )}
    </div>
  );
});

ChatHistoryList.displayName = 'ChatHistoryList';

export default ChatHistoryList;
